import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Loader2 } from 'lucide-react';
import { getPublicImpactReport } from '../services/api';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_objectif-ecosystem/artifacts/4tz74flx_LOGO%20OBJECTIF%20SCOP%20OUTREMER%202%20%283%29.png';
const COLORS = ['#00AEEF', '#80C51F', '#EA3C3A', '#FF8E00', '#D5007D'];
const TERRITORY_COLORS = { 'Guadeloupe': '#00AEEF', 'Martinique': '#80C51F', 'Guyane': '#EA3C3A', 'Réunion': '#FF8E00', 'Mayotte': '#D5007D' };
const periodLabels = { 'all': 'Toutes periodes', 'month': '30 derniers jours', 'quarter': '3 derniers mois', 'year': '12 derniers mois' };

export default function PublicImpactReportPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getPublicImpactReport(token);
        setData(res.data);
      } catch (err) {
        setError(err.response?.status === 410 ? 'Ce lien a expire.' : 'Rapport non trouve.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-[#F9F8F5] flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#F9F8F5] flex items-center justify-center">
      <Card className="max-w-md border-[#E8E6E1]">
        <CardContent className="p-8 text-center">
          <img src={LOGO_URL} alt="Logo" className="w-12 h-12 mx-auto mb-4" />
          <p className="text-lg font-semibold text-[#1A2421] mb-2">Oups !</p>
          <p className="text-sm text-[#5E6D66]">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  const { report, territories, totals } = data;

  const barData = territories.map(t => ({
    name: t.territoire.length > 8 ? t.territoire.slice(0, 8) + '.' : t.territoire,
    fullName: t.territoire,
    Organisations: t.organizations,
    Contacts: t.contacts,
    Dossiers: t.dossiers,
  }));

  const pieData = territories.filter(t => t.organizations > 0).map(t => ({
    name: t.territoire,
    value: t.organizations,
  }));

  return (
    <div className="min-h-screen bg-[#F9F8F5]" data-testid="public-impact-report">
      {/* Header */}
      <div className="bg-white border-b border-[#E8E6E1]">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-4">
            <img src={LOGO_URL} alt="Objectif SCOP Outremer" className="w-10 h-10" />
            <div>
              <p className="text-xs text-[#5E6D66] uppercase tracking-wider font-semibold">Rapport d'Impact ESS</p>
              <h1 className="text-xl sm:text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }} data-testid="report-title">
                {report.title}
              </h1>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {report.territoire && <Badge className="bg-[#2D5A4C] text-white">{report.territoire}</Badge>}
            <Badge variant="outline">{periodLabels[report.period] || report.period}</Badge>
            <Badge variant="outline" className="text-[#5E6D66]">
              Publie le {new Date(report.created_at).toLocaleDateString('fr-FR')}
            </Badge>
            {report.created_by_name && (
              <Badge variant="outline" className="text-[#5E6D66]">Par {report.created_by_name}</Badge>
            )}
          </div>
          {/* Accent bar */}
          <div className="flex gap-0.5 mt-4">
            <div className="h-1 flex-1 rounded-full" style={{ background: '#00AEEF' }} />
            <div className="h-1 flex-1 rounded-full" style={{ background: '#80C51F' }} />
            <div className="h-1 flex-1 rounded-full" style={{ background: '#EA3C3A' }} />
            <div className="h-1 flex-1 rounded-full" style={{ background: '#FF8E00' }} />
            <div className="h-1 flex-1 rounded-full" style={{ background: '#D5007D' }} />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {/* KPI Totals */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4" data-testid="report-kpis">
          {[
            { label: 'Organisations', value: totals.organizations, color: '#00AEEF' },
            { label: 'Contacts', value: totals.contacts, color: '#80C51F' },
            { label: 'Dossiers ESS', value: totals.dossiers, color: '#EA3C3A' },
            { label: 'Opportunites', value: totals.opportunities, color: '#FF8E00' },
            { label: 'Montant recu', value: `${totals.payments_amount.toLocaleString('fr-FR')} EUR`, color: '#D5007D' },
          ].map((kpi) => (
            <Card key={kpi.label} className="border-[#E8E6E1] shadow-sm overflow-hidden">
              <div className="h-1" style={{ background: kpi.color }} />
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#5E6D66] mb-1">{kpi.label}</p>
                <p className="text-2xl font-bold text-[#1A2421]">{kpi.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Territory Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="report-territory-cards">
          {territories.map((t, idx) => (
            <Card key={t.territoire} className="border-[#E8E6E1] shadow-sm overflow-hidden">
              <div className="h-1.5" style={{ background: COLORS[idx % COLORS.length] }} />
              <CardContent className="p-4">
                <h3 className="font-semibold text-sm text-[#1A2421] mb-3">{t.territoire}</h3>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between"><span className="text-[#5E6D66]">Organisations</span><span className="font-semibold">{t.organizations}</span></div>
                  <div className="flex justify-between"><span className="text-[#5E6D66]">Contacts</span><span className="font-semibold">{t.contacts}</span></div>
                  <div className="flex justify-between"><span className="text-[#5E6D66]">Dossiers</span><span className="font-semibold">{t.dossiers}</span></div>
                  <div className="flex justify-between border-t border-[#E8E6E1] pt-1.5 mt-1.5">
                    <span className="text-[#5E6D66]">Montant</span>
                    <span className="font-semibold">{t.payments_amount.toLocaleString('fr-FR')} EUR</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-[#E8E6E1] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-[#1A2421]">Comparaison par territoire</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#5E6D66' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#5E6D66' }} />
                    <Tooltip />
                    <Bar dataKey="Organisations" fill="#00AEEF" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="Contacts" fill="#80C51F" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="Dossiers" fill="#EA3C3A" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {pieData.length > 0 && (
            <Card className="border-[#E8E6E1] shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-[#1A2421]">Repartition des organisations</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={2} dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={{ stroke: '#5E6D66', strokeWidth: 1 }}
                      >
                        {pieData.map((entry, idx) => (
                          <Cell key={entry.name} fill={TERRITORY_COLORS[entry.name] || COLORS[idx % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Detail Table */}
        <Card className="border-[#E8E6E1] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1A2421]">Detail par territoire</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#E8E6E1]">
                    <th className="text-left py-2 pr-3 text-[#5E6D66] font-semibold uppercase tracking-wider">Territoire</th>
                    <th className="text-right py-2 px-2 text-[#5E6D66] font-semibold uppercase tracking-wider">Orgs</th>
                    <th className="text-right py-2 px-2 text-[#5E6D66] font-semibold uppercase tracking-wider">Contacts</th>
                    <th className="text-right py-2 px-2 text-[#5E6D66] font-semibold uppercase tracking-wider">Dossiers</th>
                    <th className="text-right py-2 px-2 text-[#5E6D66] font-semibold uppercase tracking-wider">Opps</th>
                    <th className="text-right py-2 pl-2 text-[#5E6D66] font-semibold uppercase tracking-wider">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {territories.map((t, idx) => (
                    <tr key={t.territoire} className="border-b border-[#F0EFEB] last:border-0">
                      <td className="py-2.5 pr-3">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[idx % COLORS.length] }} />
                          <span className="font-medium text-[#1A2421]">{t.territoire}</span>
                        </div>
                      </td>
                      <td className="text-right py-2.5 px-2 font-medium">{t.organizations}</td>
                      <td className="text-right py-2.5 px-2 font-medium">{t.contacts}</td>
                      <td className="text-right py-2.5 px-2 font-medium">{t.dossiers}</td>
                      <td className="text-right py-2.5 px-2 font-medium">{t.opportunities}</td>
                      <td className="text-right py-2.5 pl-2 font-semibold">{t.payments_amount.toLocaleString('fr-FR')} EUR</td>
                    </tr>
                  ))}
                  <tr className="bg-[#F9F8F5] font-semibold">
                    <td className="py-2.5 pr-3">Total</td>
                    <td className="text-right py-2.5 px-2">{totals.organizations}</td>
                    <td className="text-right py-2.5 px-2">{totals.contacts}</td>
                    <td className="text-right py-2.5 px-2">{totals.dossiers}</td>
                    <td className="text-right py-2.5 px-2">{totals.opportunities}</td>
                    <td className="text-right py-2.5 pl-2">{totals.payments_amount.toLocaleString('fr-FR')} EUR</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center pt-4 pb-8 border-t border-[#E8E6E1]">
          <div className="flex items-center justify-center gap-2 mb-2">
            <img src={LOGO_URL} alt="Logo" className="w-6 h-6" />
            <span className="text-sm font-semibold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Objectif SCOP Outremer</span>
          </div>
          <p className="text-xs text-[#5E6D66]">Rapport genere automatiquement par le CRM ESS</p>
        </div>
      </div>
    </div>
  );
}
